/* @ts-ignore */
import queryStringHandler from "query-string-handler";
import { scrollToElement } from "@/components/global/scroll-to-element";

interface RecentCoverageLink {
  href?: string;
  target?: string;
  text?: string;
  title?: string;
}

interface RecentCoverageItem {
  title?: string;
  date?: string;
  companySource?: string;
  dateDisplay?: string;
  link?: RecentCoverageLink;
}

interface RecentCoverageResponse {
  currentPage: number;
  totalPages: number;
  items?: RecentCoverageItem[];
}

const setPaginationButtonState = (prevBtn: HTMLButtonElement | null, nextBtn: HTMLButtonElement | null, currentPage: number, totalPages: number) => {
  if (prevBtn) {
    prevBtn.disabled = currentPage <= 1;
  }
  if (nextBtn) {
    nextBtn.disabled = currentPage >= totalPages;
  }
};

export default function RecentCoveragePage() {
  const wrapper = document.querySelector<HTMLDivElement>(".js-recent-coverage-page");
  if (!wrapper) return;

  const prevBtn = wrapper.querySelector<HTMLButtonElement>(".js-prev-btn");
  const nextBtn = wrapper.querySelector<HTMLButtonElement>(".js-next-btn");
  const listEl = wrapper.querySelector<HTMLUListElement>(".js-recent-coverage-tbody");

  if (!prevBtn || !nextBtn || !listEl) return;

  const totalPages = parseInt(wrapper.getAttribute("data-total-pages") || "0", 10);
  if (totalPages < 2) return;

  let currentPage = parseInt(queryStringHandler.getValue("pg") || wrapper.getAttribute("data-current-page") || "1", 10);

  const handleCreateCoverageRow = (item: RecentCoverageItem): string => {
    let titleCell = "";
    if (item.link?.href != null) {
      const linkText = item.link.text != null && item.link.text !== "" ? item.link.text : (item.title ?? "");
      titleCell = `<a href="${item.link.href}" target="${item.link.target || "_blank"}" aria-label="${item.link.title ?? ""}" class="text-primary-700 hover:text-primary-800 h6 underline">${linkText}</a>`;
    } else if (item.title != null && item.title !== "") {
      titleCell = `<span class="h6">${item.title}</span>`;
    }

    const company = item.companySource ?? "";
    const date = item.dateDisplay ?? "";

    return `
    <li class="flex flex-col md:flex-row gap-3 md:gap-6 border-b border-neutral-100 py-4 body md:body-lg text-neutral-700">
      <div class="order-1 md:order-0 flex-1">${titleCell}</div>
      <div class="order-3 md:order-2 md:flex-1 xl:flex-none xl:w-[358px] break-words">${company}</div>
      <div class="order-0 md:order-3 md:w-[108px] whitespace-nowrap">${date}</div>
    </li>
  `;
  };

  const handleFetchPage = async (page: string) => {
    try {
      const url = `/api/search/recent-coverage?pg=${page}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const data: RecentCoverageResponse = await response.json();

      listEl.innerHTML = "";
      if (data.items?.length) {
        data.items.forEach((item) => {
          listEl.insertAdjacentHTML("beforeend", handleCreateCoverageRow(item));
        });
      }

      currentPage = data.currentPage;
      setPaginationButtonState(prevBtn, nextBtn, currentPage, data.totalPages);
      scrollToElement("recent-coverage-page");
    } catch (err) {
      console.error("Recent coverage fetch error:", err);
    }
  };

  const handlePrevPage = () => {
    if (currentPage <= 1) return;
    const nextPage = String(currentPage - 1);
    queryStringHandler.replace({ pg: nextPage });
    handleFetchPage(nextPage);
  };

  const handleNextPage = () => {
    if (currentPage >= totalPages) return;
    const nextPage = String(currentPage + 1);
    queryStringHandler.replace({ pg: nextPage });
    handleFetchPage(nextPage);
  };

  setPaginationButtonState(prevBtn, nextBtn, currentPage, totalPages);
  prevBtn.addEventListener("click", handlePrevPage);
  nextBtn.addEventListener("click", handleNextPage);
}
